import type { TTemplateVariable } from "../../Integrations"

/**
 * Regex matching NAMED template variables in `{{variable_name}}` format.
 * Numeric-only keys are excluded so positional placeholders (`{{1}}`,
 * `{{2}}`) do not collide with the named extractor.
 */
const VARIABLE_REGEX = /\{\{([a-z_][a-z0-9_]*)\}\}/gi

/**
 * Regex matching POSITIONAL template variables in `{{1}}`, `{{2}}` form.
 */
const POSITIONAL_VARIABLE_REGEX = /\{\{(\d+)\}\}/g

/**
 * Extract NAMED variable keys from template text, deduplicated, in
 * order of appearance.
 */
export function extractVariables(text: string): string[] {
	if (!text) return []

	const matches = text.matchAll(VARIABLE_REGEX)
	const variables: string[] = []

	for (const match of matches) {
		if (match[1]) {
			variables.push(match[1])
		}
	}

	return Array.from(new Set(variables))
}

/**
 * Extract POSITIONAL variable indices (`"1"`, `"2"`, ...) from template
 * text, deduplicated, in order of appearance. Returns numeric strings
 * so call sites match the data shapes used in `body_text` /
 * `header_text` / `footer_text` per Meta.
 */
export function extractPositionalVariables(text: string): string[] {
	if (!text) return []

	const matches = text.matchAll(POSITIONAL_VARIABLE_REGEX)
	const variables: string[] = []

	for (const match of matches) {
		if (match[1]) {
			variables.push(match[1])
		}
	}

	return Array.from(new Set(variables))
}

/**
 * Validate the variables in a template. `wildcardPrefix` (e.g. "extras_")
 * allows any variable whose key starts with the prefix and has a non-empty
 * suffix — used by sources that expose ad-hoc fields from a JSON column.
 *
 * `parameterFormat`:
 * - "NAMED" (default) — uses `extractVariables` and validates against
 *   `allowedVariables` plus the wildcard prefix.
 * - "POSITIONAL" — uses `extractPositionalVariables`. Indices have no
 *   data-source registry to validate against, so all positional variables
 *   are considered structurally valid (existence-of-example checks live
 *   per-block in the editor).
 */
export function validateTemplate(
	text: string,
	allowedVariables: TTemplateVariable[],
	wildcardPrefix?: string | null,
	parameterFormat: "NAMED" | "POSITIONAL" = "NAMED"
): {
	valid: boolean
	invalidVariables: string[]
	errors?: string[]
} {
	if (parameterFormat === "POSITIONAL") {
		// All numeric placeholders are inherently valid positional refs.
		return { valid: true, invalidVariables: [] }
	}

	const usedVariables = extractVariables(text)
	const allowedKeys = allowedVariables.map((v) => v.key)
	const prefix = wildcardPrefix?.trim() || ""

	const invalidVariables = usedVariables.filter((v) => {
		if (allowedKeys.includes(v)) return false
		if (prefix && v.startsWith(prefix) && v.length > prefix.length) {
			return false
		}
		return true
	})

	return {
		valid: invalidVariables.length === 0,
		invalidVariables,
		errors:
			invalidVariables.length > 0
				? [
						`Invalid variables found: ${invalidVariables.map((v) => `{{${v}}}`).join(", ")}`,
					]
				: undefined,
	}
}

/**
 * Replace variables in template with actual or sample values
 */
export function resolveVariables(
	text: string,
	variables: TTemplateVariable[]
): string {
	if (!text) return ""

	let resolved = text

	variables.forEach((variable) => {
		const value = variable.sample_value
		const regex = new RegExp(`\\{\\{${variable.key}\\}\\}`, "gi")
		resolved = resolved.replace(regex, value)
	})

	return resolved
}

export function hasVariables(text: string): boolean {
	return VARIABLE_REGEX.test(text)
}

function escapeRegex(str: string): string {
	return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

/**
 * Replace a specific variable in template
 */
export function replaceVariable(
	text: string,
	variableKey: string,
	value: string
): string {
	const regex = new RegExp(`\\{\\{${escapeRegex(variableKey)}\\}\\}`, "gi")
	return text.replace(regex, value)
}

/**
 * Validate that variables are not at start or end of message
 * Facebook/WhatsApp Business API requirement
 */
export function validateVariablePosition(text: string): {
	valid: boolean
	startsWithVariable: boolean
	endsWithVariable: boolean
	error?: string
} {
	if (!text || text.trim().length === 0) {
		return { valid: true, startsWithVariable: false, endsWithVariable: false }
	}

	const trimmedText = text.trim()

	const startsWithVariable = /^\{\{[a-z_][a-z0-9_]*\}\}/i.test(trimmedText)

	const endsWithVariable = /\{\{[a-z_][a-z0-9_]*\}\}$/i.test(trimmedText)

	let error: string | undefined
	if (startsWithVariable && endsWithVariable) {
		error = "Variables can't be at the start or end of the template."
	} else if (startsWithVariable) {
		error = "Variables can't be at the start of the template."
	} else if (endsWithVariable) {
		error = "Variables can't be at the end of the template."
	}

	return {
		valid: !startsWithVariable && !endsWithVariable,
		startsWithVariable,
		endsWithVariable,
		error,
	}
}
